import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { get, put } from '../../../helpers/apiHelper.ts'
import { sendCommand } from '../../../helpers/commandHelper.ts'
import { STRIPE_GET_SECRET_URL, STRIPE_UPDATE_PAYMENT_URL } from '../../../helpers/urls.ts'
import authStore from '../../../store/authStore.ts'
import controlStore from '../../../store/controlStore.ts'
import dataStore from '../../../store/dataStore.ts'
import { Section } from '../../../forms/v2/components/Section.tsx'
import { appearance } from '../../../forms/v2/multiStep/stripeElementConfig.ts'
import { ExpirationDate } from '../../../forms/v2/widgets/ExpirationDate.tsx'
import { PopupPortal } from '../Popup/PopupPortal.tsx'
import { gibiStripeOptions } from '../../../Router/stripeElementOptions.ts'

const stripe = loadStripe(import.meta.env.VITE_STRIPE_PUBLISH_KEY as string)

const parseExpDate = (exp: string) => {
    if (exp) {
        const bits = exp.split(' ')
        if (bits.length === 3) {
            const my = bits[2].split('/')
            return { month: my[0], year: my[1] }
        }
    }
    return { month: '', year: '' }
}

interface Props {
    open: boolean
    setOpen: (o: boolean) => void
}

const ManagePaymentsContent: React.FC<Props> = ({ open, setOpen }) => {
    const paymentMethod = dataStore(s => s.paymentMethod)
    const token = authStore(s => s.token)
    const stripeHook = useStripe()
    const elements = useElements()

    // @ts-ignore
    elements?.update({ appearance })

    const [origExp, setOrigExp] = React.useState({ month: '', year: '' })
    const [clientSecret, setClientSecret] = React.useState('')
    const [validClient, setValidClient] = React.useState(false)
    const [newCard, setNewCard] = React.useState(false)
    const [error, setError] = React.useState('')

    React.useEffect(() => {
        if (open && paymentMethod) {
            setOrigExp(parseExpDate(paymentMethod))
        }
        if (!open) {
            setClientSecret('')
            setValidClient(false)
            setNewCard(false)
            setError('')
        }
    }, [open, paymentMethod])

    React.useEffect(() => {
        if (!open) return
        try {
            const controller = new AbortController()
            void get(STRIPE_GET_SECRET_URL, undefined, controller).then((d: any) => {
                setClientSecret(d?.data?.clientSecret || 'error')
                if (d?.data?.clientSecret && d.data?.setClientSecret !== 'error') {
                    setValidClient(true)
                } else {
                    setError('Payment management not available, try again later')
                }
            })
            setTimeout(() => {
                if (!validClient) {
                    controller.abort()
                }
            }, 2500)
        } catch (e) {
            console.log(e)
            controlStore.getState().setShowLoader(false)
        }
    }, [open, token])

    const {
        formState: { errors },
        watch,
        control,
    } = useForm({
        defaultValues: {
            expiration: parseExpDate(paymentMethod),
        },
        mode: 'onChange',
        reValidateMode: 'onBlur',
    })

    const expiration = watch('expiration')

    const createPaymentMethod = async () => {
        if (!stripeHook || !elements || !clientSecret) {
            return
        }
        const { error: submitError } = await elements.submit()
        if (submitError) return

        const eleCard = elements.getElement(CardElement)
        const result = await stripeHook.confirmCardSetup(clientSecret, {
            payment_method: {
                // @ts-ignore
                card: eleCard,
                billing_details: {},
            }
        })
        if (result.error) {
            toast.error(result.error.message || 'Card setup failed')
        } else {
            const data = { paymentMethod: result.setupIntent.payment_method }
            const response: any = await put(STRIPE_UPDATE_PAYMENT_URL, data)
            if (response?.status === 200) {
                if (response.data.paymentMethod) {
                    dataStore.getState().setPaymentMethod(response.data.paymentMethod)
                }
                toast.success('Credit card updated')
            } else {
                toast.error('Error, credit card not updated. Try again later')
            }
            setOpen(false)
        }
    }

    const panelHeader = (
        <div className={'prose flex flex-col justify-start items-center gap-1 pb-2'}>
            <h2 className={'mt-0 mb-0 text-primary'}>Manage Payments</h2>
        </div>
    )

    const panel = (
        <div className={'flex flex-col justify-start items-center gap-4 w-full p-6'}>
            {panelHeader}

            <Section title={'Current payment method'}>
                <div className={'font-semibold text-lg'}>
                    {error || paymentMethod}
                </div>
            </Section>

            <Section title={'Change expiration date'} disabled={!validClient}>
                <ExpirationDate
                    control={control}
                    formField={'expiration'}
                    title={''}
                    errors={errors}
                    disabled={!validClient}
                />
                <button
                    className={'btn btn-sm text-primary btn-ghost mt-4'}
                    disabled={expiration.month === origExp.month && expiration.year === origExp.year}
                    onClick={async (e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        if (expiration) {
                            const data = await sendCommand('expiration', '', expiration)
                            if (typeof data !== 'string') {
                                dataStore.getState().setPaymentMethod(data.paymentMethod)
                                toast.success('Expiration date updated')
                            } else {
                                toast.error('Error, Expiration date update failed, try again later.')
                            }
                            setOpen(false)
                        }
                    }}
                >
                    Update Exp Date
                </button>
            </Section>

            <Section title={'Replace current payment method'} disabled={!validClient}>
                <div className={'bg-white mt-2'}>
                    <CardElement onChange={(event) => {
                        if (validClient) {
                            setNewCard(event?.complete || false)
                        }
                    }} />
                </div>
                <button
                    className={'btn btn-sm text-primary btn-ghost mt-4'}
                    disabled={!newCard}
                    onClick={async (e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        await createPaymentMethod()
                    }}
                >
                    Update payment method
                </button>
            </Section>
        </div>
    )

    const buttons = (
        <div className={'px-6 pb-6 pt-2 flex justify-center w-full'}>
            <button className={'btn btn-ghost btn-sm text-secondary'} onClick={() => setOpen(false)}>
                Close
            </button>
        </div>
    )

    return (
        <PopupPortal open={open}>
            {panel}
            {buttons}
        </PopupPortal>
    )
}

export const ManagePaymentsPopup: React.FC<Props> = ({ open, setOpen }) => {
    if (!open) return null
    return (
        <Elements stripe={stripe} options={gibiStripeOptions}>
            <ManagePaymentsContent open={open} setOpen={setOpen} />
        </Elements>
    )
}
